import React, { useState, useEffect, useMemo } from "react";
import { Tooltip, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  root: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: "0.7rem",
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
}));

export const SLA_COLORS = {
  green: "#4CAF50",
  yellow: "#FFC107",
  orange: "#FF9800",
  red: "#F44336",
};

export const DEFAULT_SLA = { green: 15, yellow: 30, orange: 60, red: 120 };

/**
 * Formata duração em minutos para texto legível
 */
const formatDuration = (totalMin) => {
  if (totalMin < 1) return "< 1min";
  if (totalMin < 60) return `${Math.floor(totalMin)}min`;
  const hours = Math.floor(totalMin / 60);
  const mins = Math.floor(totalMin % 60);
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const rHours = hours % 24;
  return rHours > 0 ? `${days}d ${rHours}h` : `${days}d`;
};

/**
 * Retorna a cor SLA com base nos minutos decorridos e thresholds.
 *
 * Cada valor define o LIMITE daquela faixa (tempo máximo antes de escalar):
 *   0 → verde(2):  tudo ok, sem alerta
 *   verde(2) → amarelo(30):  alerta amarelo
 *   amarelo(30) → laranja(60):  alerta laranja
 *   laranja(60) → vermelho(120): alerta vermelho
 *   vermelho(120)+: vermelho crítico
 */
export const getSlaLevel = (elapsedMin, sla) => {
  const cfg = { ...DEFAULT_SLA, ...sla };
  if (elapsedMin >= cfg.red) return "red";
  if (elapsedMin >= cfg.orange) return "red";
  if (elapsedMin >= cfg.yellow) return "orange";
  if (elapsedMin >= cfg.green) return "yellow";
  return "green";
};

/**
 * SlaTimer — exibe tempo decorrido desde enteredLaneAt com cor baseada em SLA.
 *
 * Props:
 *  - enteredLaneAt: ISO string ou Date
 *  - slaConfig: { green, yellow, orange, red } em minutos
 *  - dotOnly: boolean — se true, mostra apenas o dot colorido (compacto)
 */
const SlaTimer = ({ enteredLaneAt, slaConfig, dotOnly = false }) => {
  const classes = useStyles();
  const [now, setNow] = useState(() => Date.now());

  // Re-render a cada 60s (apenas o timer, não o card inteiro)
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  const result = useMemo(() => {
    if (!enteredLaneAt) return null;
    const start = typeof enteredLaneAt === "string"
      ? new Date(enteredLaneAt).getTime()
      : enteredLaneAt.getTime ? enteredLaneAt.getTime() : 0;
    if (!start || isNaN(start)) return null;

    const elapsedMin = (now - start) / 60000;
    const level = getSlaLevel(elapsedMin, slaConfig);
    const color = SLA_COLORS[level];
    const text = formatDuration(elapsedMin);

    return { color, text, level, elapsedMin };
  }, [enteredLaneAt, slaConfig, now]);

  if (!result) return null;

  const tooltipText = `Na lane há ${result.text} (SLA: ${result.level})`;

  if (dotOnly) {
    return (
      <Tooltip title={tooltipText}>
        <span
          className={classes.dot}
          style={{ backgroundColor: result.color }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipText}>
      <span className={classes.root} style={{ color: result.color }}>
        <span
          className={classes.dot}
          style={{ backgroundColor: result.color }}
        />
        {result.text}
      </span>
    </Tooltip>
  );
};

export default React.memo(SlaTimer);
